import { useTranslation } from 'react-i18next';

import { Button, Flex, Icon, Text } from '@kognitos/lattice';

/**
 * Props for {@link AutomationTestPane}.
 */
export interface IAutomationTestPaneProps {
  /** Which version is tested. The live version is tested in the workbench only. */
  version?: 'draft' | 'live';
  /** Opens the test inputs and sends a test run through the chat. Omit where no chat is mounted. */
  onTest?: () => void;
  canTest: boolean;
  isTesting: boolean;
  /** Opens the whole-automation workbench. Omit to hide it. */
  onOpenWorkbench?: () => void;
}

/**
 * The document's Test tab: test this draft with inputs, or open the
 * workbench. A test runs through the builder chat, so its progress and
 * result show there and on the document. The live version has only the
 * workbench, since a chat test runs the draft.
 */
export function AutomationTestPane({
  version = 'draft',
  onTest,
  canTest,
  isTesting,
  onOpenWorkbench,
}: IAutomationTestPaneProps) {
  const { t: tAutomation } = useTranslation('automation');
  const isLive = version === 'live';

  return (
    <Flex
      direction="column"
      gap={4}
      width="full"
      className="min-h-0 flex-1 overflow-y-auto px-10 py-8"
      data-testid="automation-test-pane"
    >
      <Flex
        direction="column"
        gap={3}
        width="full"
        className="mx-auto max-w-[720px] rounded-xl border border-border bg-secondary/50 p-5"
      >
        <Text type="eyebrow">
          {tAutomation(
            isLive
              ? 'automation.workspace.test.liveTitle'
              : 'automation.workspace.test.title',
          )}
        </Text>
        <Text level="small" color="muted">
          {tAutomation(
            isLive
              ? 'automation.workspace.test.liveDescription'
              : 'automation.workspace.test.description',
          )}
        </Text>
        <Flex align="center" gap={2} className="flex-wrap">
          {onTest && (
            <Button
              onClick={onTest}
              disabled={!canTest || isTesting}
              isLoading={isTesting}
              data-testid="draft-rail-test-button"
            >
              <Icon type="FlaskConical" size="sm" />
              {tAutomation('automation.workspace.test.run')}
            </Button>
          )}
          {onOpenWorkbench && (
            <Button
              variant="outline"
              onClick={onOpenWorkbench}
              data-testid="test-automation-workbench-button"
            >
              <Icon type="ScanSearch" size="sm" />
              {tAutomation('automation.header.tooltip.testAutomation')}
            </Button>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}
